import { useState } from 'react'
import type { FieldValues, Path, Control } from 'react-hook-form'

import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from 'src/shared/components/ui/forms/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/shared/components/ui/select'
import type { MapItem } from 'src/shared/schemas/utils'
import { cn } from 'src/shared/utils/cn'

import { Label } from '../../label'

interface SelectFormFieldProps<T extends FieldValues> {
  control?: Control<T>
  name: Path<T>
  label: string
  options: Record<string, MapItem>
  description?: string
  className?: string
  fieldClassName?: string
  triggerClassName?: string
  valueClassName?: string
  labelClassName?: string
  disabled?: boolean
  required?: boolean
  onValueChange?: (value: string) => void
  variant?: 'old' | 'new'
}

export function SelectFormField<T extends FieldValues>({
  control,
  name,
  label,
  options,
  description,
  className,
  fieldClassName,
  triggerClassName,
  labelClassName,
  valueClassName,
  disabled,
  required = false,
  onValueChange,
  variant = 'old',
}: SelectFormFieldProps<T>) {
  const [isOpen, setIsOpen] = useState(false)

  if (required) {
    label = `${label} *`
  }

  if (variant === 'new') {
    return (
      <FormField
        control={control}
        name={name}
        render={({ field }) => {
          const placeholderShown = field.value ? false : true
          const value = field.value && options[field.value] ? options[field.value].label : ''

          return (
            <FormItem className={cn('w-full space-y-1', className)}>
              <FormControl>
                <div className={cn('relative', fieldClassName)}>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value)
                      onValueChange?.(value)
                    }}
                    value={field.value}
                    disabled={disabled}
                    onOpenChange={(value) => {
                      setIsOpen(value)
                    }}
                  >
                    <SelectTrigger
                      className={cn('h-[60px] rounded-[12px] border-none bg-accent', triggerClassName)}
                      iconClassName="size-5 opacity-100 text-primary mr-1"
                    >
                      <Label
                        className={cn(
                          'pointer-events-none absolute start-1.5 top-2 z-10 origin-[0] -translate-y-[1px] scale-75 transform cursor-text bg-transparent px-3 text-sm font-medium text-muted duration-300',
                          'dark:bg-background rtl:peer-focus:left-auto rtl:peer-focus:translate-x-1/4',
                          '[.input-with-icon_&]:start-8',
                          placeholderShown && 'start-1 top-1/2 -translate-y-1/2 scale-100 text-[16px]',
                          isOpen && 'top-2 -translate-y-[1px] scale-75 px-3',
                          labelClassName,
                        )}
                      >
                        {label}
                      </Label>
                      <span
                        className={cn(
                          'ml-1 mt-3 text-[16px]',
                          field.value ? 'text-primary' : 'text-muted',
                          valueClassName,
                        )}
                      >
                        {value}
                      </span>
                    </SelectTrigger>
                    <SelectContent
                      showScrollButtons={false}
                      contentClassName="p-0"
                      className="mt-[6px] max-h-[300px] rounded-[12px] border border-lightBorder shadow-[0_0_15px_0_rgba(27,48,41,0.07)]"
                    >
                      {Object.entries(options).map(([value, { label }]) => (
                        <SelectItem
                          key={value}
                          value={value}
                          showCheckmark={false}
                          className="h-[46px] cursor-pointer pl-4 text-[15px] text-primary"
                        >
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </FormControl>
              {description && <FormDescription>{description}</FormDescription>}
              <FormMessage />
            </FormItem>
          )
        }}
      />
    )
  }

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn('w-full', className)}>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <div className={cn('relative', fieldClassName)}>
              <Select
                onValueChange={(value) => {
                  field.onChange(value)
                  onValueChange?.(value)
                }}
                value={field.value}
                disabled={disabled}
              >
                <SelectTrigger className="bg-accent">
                  <SelectValue placeholder={'Select'} />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(options).map(([value, { label }]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
